import {useCallback, useEffect, useRef, useState} from 'react'

type UseDragOverReturn = {
  isDragOver: boolean
  handleDrop: (e: React.DragEvent<HTMLDivElement>) => File | null
  setIsDragOver: React.Dispatch<React.SetStateAction<boolean>>
}

export const useDragOver = (): UseDragOverReturn => {
  const [isDragOver, setIsDragOver] = useState(false)
  const dragCounter = useRef(0)

  const handleDragEnter = useCallback((e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current++
    if (e.dataTransfer?.items && e.dataTransfer.items.length > 0) {
      setIsDragOver(true)
    }
  }, [])

  const handleDragLeave = useCallback((e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    dragCounter.current--
    if (dragCounter.current === 0) {
      setIsDragOver(false)
    }
  }, [])

  const handleDragOver = useCallback((e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    if (e.dataTransfer) {
      e.dataTransfer.dropEffect = 'move'
    }
  }, [])

  const handleDocumentDrop = useCallback((e: DragEvent) => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
    dragCounter.current = 0
  }, [])

  useEffect(() => {
    document.addEventListener('dragenter', handleDragEnter)
    document.addEventListener('dragleave', handleDragLeave)
    document.addEventListener('dragover', handleDragOver)
    document.addEventListener('drop', handleDocumentDrop)

    return () => {
      document.removeEventListener('dragenter', handleDragEnter)
      document.removeEventListener('dragleave', handleDragLeave)
      document.removeEventListener('dragover', handleDragOver)
      document.removeEventListener('drop', handleDocumentDrop)
    }
  }, [handleDragEnter, handleDragLeave, handleDragOver, handleDocumentDrop])

  const handleDrop = useCallback((e: React.DragEvent<HTMLDivElement>): File | null => {
    e.preventDefault()
    e.stopPropagation()
    setIsDragOver(false)
    dragCounter.current = 0

    const file = e.dataTransfer?.files?.[0]
    return file || null
  }, [])

  return {isDragOver, handleDrop, setIsDragOver}
}
